import { useId, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { RenderHtml } from "@/components/RenderHtml";
import { ExternalLink } from "@/components/Button/ExternalLink";
import type { RouterOutputs } from "@/trpc";

type Job = NonNullable<RouterOutputs["jobPost"]["orgGet"]>;

function JobDetail({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-2">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <div className="text-sm leading-6 text-slate-600 [overflow-wrap:anywhere]">
        {children}
      </div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-2 pl-5">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export function JobDetailsDisclosure({ job }: { job: Job }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const detailsId = useId();

  return (
    <section
      aria-label="Job details"
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
    >
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={detailsId}
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-primary hover:bg-primary/5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-5"
      >
        <span>
          Job details{" "}
          <span className="font-normal text-slate-500">
            &amp; internal notes
          </span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-5 w-5 shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={detailsId}
        hidden={!isExpanded}
        className="space-y-6 border-t border-slate-200 p-4 sm:p-5"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <JobDetail title="Publication">
            <p>
              {job.publishedAt
                ? `Posted ${format(new Date(job.publishedAt), "MMM d, yyyy")}`
                : "Draft - Not yet published"}
            </p>
            <p className="mt-1 font-mono text-xs text-slate-500">
              ID: {job.id}
            </p>
          </JobDetail>
          {job.scheduleDescription && (
            <JobDetail title="Schedule">
              <div className="prose prose-sm max-w-none text-slate-600">
                <RenderHtml html={job.scheduleDescription} />
              </div>
            </JobDetail>
          )}
          {job.hiringProcess && (
            <JobDetail title="Hiring process">
              {job.hiringProcess.name}
            </JobDetail>
          )}
          {job.externalUrl && (
            <JobDetail title="External link">
              <ExternalLink
                href={job.externalUrl}
                className="text-primary underline-offset-4 hover:underline"
              >
                {job.externalUrl}
              </ExternalLink>
            </JobDetail>
          )}
        </div>
        {job.organizationNotes && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <JobDetail title="Internal Notes">
              <p className="whitespace-pre-wrap">{job.organizationNotes}</p>
            </JobDetail>
          </div>
        )}
        {job.description && (
          <JobDetail title="Description">
            <div className="prose prose-sm max-w-none text-slate-600">
              <RenderHtml html={job.description} />
            </div>
          </JobDetail>
        )}
        {job.responsibilities && job.responsibilities.length > 0 && (
          <JobDetail title="Responsibilities">
            <BulletList items={job.responsibilities} />
          </JobDetail>
        )}
        {job.requiredQualifications &&
          job.requiredQualifications.length > 0 && (
            <JobDetail title="Required Qualifications">
              <BulletList items={job.requiredQualifications} />
            </JobDetail>
          )}
        {job.preferredQualifications &&
          job.preferredQualifications.length > 0 && (
            <JobDetail title="Preferred Qualifications">
              <BulletList items={job.preferredQualifications} />
            </JobDetail>
          )}
      </div>
    </section>
  );
}
